import { LitElement, html, css } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { sortRows, paginate, toCsv, csvFilename } from '../lib/table.js';

/**
 * The table every chart has to have (PRD 7.3), and the thing that exports. Give it columns and
 * rows; it sorts, pages, and hands back a CSV of the whole set — not just the page on screen,
 * because an export that silently dropped rows would be worse than none.
 *
 * Columns: { key, label, type: 'text'|'number'|'date', align?, format?(value, row), csv?(value, row) }
 *
 * The download is wired here, but a view can listen for `waag:export` and call
 * `preventDefault()` to export server-side instead (Phase 37).
 */
class DataTable extends LitElement {
  static properties = {
    columns: { type: Array },
    rows: { type: Array },
    caption: { type: String },
    exportName: { type: String, attribute: 'export-name' },
    pageSize: { type: Number, attribute: 'page-size' },
    sortKey: { type: String, attribute: 'sort-key' },
    sortDirection: { type: String, attribute: 'sort-direction' },
    dense: { type: Boolean },
    _page: { state: true },
  };

  constructor() {
    super();
    this.columns = [];
    this.rows = [];
    this.caption = '';
    this.exportName = 'table';
    this.pageSize = 25;
    this.sortKey = null;
    this.sortDirection = 'asc';
    this.dense = false;
    this._page = 1;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .head {
        display: flex;
        align-items: baseline;
        gap: 12px;
        margin-bottom: 8px;
      }
      .caption {
        font-size: 13px;
        color: var(--ink-muted);
      }
      .actions {
        margin-left: auto;
        display: flex;
        gap: 8px;
      }
      button {
        font: inherit;
        font-size: 13px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 5px 10px;
        cursor: pointer;
      }
      button[disabled] {
        opacity: 0.5;
        cursor: default;
      }

      /* A table is the one thing allowed to scroll sideways; the page never does. */
      .scroll {
        overflow-x: auto;
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        background: var(--surface);
      }
      table {
        border-collapse: collapse;
        width: 100%;
        font-size: 13px;
      }
      caption {
        text-align: left;
        padding: 0 0 8px;
        color: var(--ink-muted);
      }
      th,
      td {
        text-align: left;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-soft);
        white-space: nowrap;
      }
      :host([dense]) th,
      :host([dense]) td {
        padding: 5px 10px;
      }
      thead th {
        position: sticky;
        top: 0;
        background: var(--surface-2);
        font-family: var(--font-eyebrow);
        font-size: 10.5px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--ink-muted);
        cursor: pointer;
        user-select: none;
      }
      thead th .dir {
        color: var(--ink);
        margin-left: 4px;
      }
      td.number,
      th.number {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      tbody tr:hover td {
        background: var(--surface-2);
      }
      tbody tr:last-child td {
        border-bottom: 0;
      }
      .empty {
        padding: 24px 12px;
        text-align: center;
        color: var(--ink-muted);
      }
      .foot {
        display: flex;
        align-items: center;
        gap: 10px;
        padding-top: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .foot .pager {
        margin-left: auto;
        display: flex;
        gap: 6px;
        align-items: center;
      }
    `,
  ];

  get _sorted() {
    const column = this.columns.find((c) => c.key === this.sortKey);
    return column ? sortRows(this.rows ?? [], column, this.sortDirection) : (this.rows ?? []);
  }

  _sortBy(column) {
    if (this.sortKey === column.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = column.key;
      // Numbers are almost always interesting from the top; text from A.
      this.sortDirection = column.type === 'number' ? 'desc' : 'asc';
    }
    this._page = 1;
  }

  _export() {
    const rows = this._sorted;
    const event = new CustomEvent('waag:export', {
      detail: { rows, columns: this.columns, name: this.exportName },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    if (!this.dispatchEvent(event)) return; // a view took over (Phase 37 server-side export)

    const csv = toCsv(rows, this.columns);
    // A byte-order mark, so Excel opens a UTF-8 CSV as UTF-8 and not as Latin-1.
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = csvFilename(this.exportName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  }

  render() {
    const page = paginate(this._sorted, { page: this._page, pageSize: this.pageSize });
    const cell = (column, row) => {
      const raw = row[column.key];
      return column.format ? column.format(raw, row) : (raw ?? '—');
    };

    return html`
      <div class="head">
        ${this.caption ? html`<span class="caption">${this.caption}</span>` : ''}
        <span class="actions">
          <button @click=${this._export} ?disabled=${!this.rows?.length}>Download CSV</button>
        </span>
      </div>

      <div class="scroll">
        <table>
          <thead>
            <tr>
              ${this.columns.map(
                (column) => html`
                  <th
                    class=${column.type === 'number' ? 'number' : ''}
                    aria-sort=${
                      this.sortKey === column.key
                        ? this.sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                    @click=${() => this._sortBy(column)}
                  >
                    ${column.label}
                    ${
                      this.sortKey === column.key
                        ? html`<span class="dir">${this.sortDirection === 'asc' ? '▲' : '▼'}</span>`
                        : ''
                    }
                  </th>
                `,
              )}
            </tr>
          </thead>
          <tbody>
            ${
              page.rows.length === 0
                ? html`
                    <tr>
                      <td class="empty" colspan=${this.columns.length}>Nothing to show.</td>
                    </tr>
                  `
                : page.rows.map(
                    (row) => html`
                      <tr>
                        ${this.columns.map(
                          (column) => html`
                            <td class=${column.type === 'number' ? 'number' : ''}>
                              ${cell(column, row)}
                            </td>
                          `,
                        )}
                      </tr>
                    `,
                  )
            }
          </tbody>
        </table>
      </div>

      <div class="foot">
        <span>${page.total === 0 ? 'No rows' : `${page.from}–${page.to} of ${page.total}`}</span>
        ${
          page.pages > 1
            ? html`
                <span class="pager">
                  <button ?disabled=${page.page === 1} @click=${() => (this._page = page.page - 1)}>
                    Previous
                  </button>
                  <span>Page ${page.page} of ${page.pages}</span>
                  <button
                    ?disabled=${page.page === page.pages}
                    @click=${() => (this._page = page.page + 1)}
                  >
                    Next
                  </button>
                </span>
              `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('data-table', DataTable);
